import type {
  SubQuestionResponseEntry,
  UpdateAuditQuestionResponseCommandInput,
} from '@amzn/gbsa-helios-typescript-client';
import type {
  PreviousResponseForSave,
  TopLevelSaveFields,
} from './deriveTopLevelFieldsForSave';
import {
  buildPreservedResponseData,
  projectSubResponsesForPreservation,
  type NaReasonOverride,
} from './preserveResponseFields';

/**
 * Arguments for {@link buildNaReasonUpdate}. Bundled in a single
 * object so future fields (attachments draft, per-sub result
 * override, `expectedLastUpdatedAt`) slot in without shifting the
 * caller signature.
 */
export interface BuildNaReasonUpdateArgs {
  readonly businessUnitId: string;
  readonly auditId: string;
  readonly questionId: string;
  /** Current user alias — sent as `respondedBy` / `lastUpdatedBy`. */
  readonly alias: string;
  /**
   * The draft NA-reason map. Keys are either the parent
   * `questionId` (parent-level reason slot) or a `subQuestionId`
   * (per-sub reason slot). Values follow the tri-state:
   *
   *   - absent key → PRESERVE the persisted `naReason` label
   *     (the read shape carries only the label; the id is lost
   *     on the server round-trip and stays empty here too).
   *   - `null`      → CLEAR both `naReason` and `naReasonId`.
   *   - `{reasonId, label}` → REPLACE both fields.
   *
   * The map is expected to be complete (parent + one entry per
   * sub-question) in practice — {@link useNaReasonDraft} seeds a
   * slot per rendered picker. The helper still tolerates sparse
   * maps: any key not in the map falls through to preservation
   * on that slot, which is the safe default for a full-PATCH
   * write.
   */
  readonly naReasonDraft: Readonly<Record<string, NaReasonOverride>>;
  /**
   * Pre-computed top-level fields for the wire — produced by the
   * shared {@link deriveTopLevelFieldsForSave} factory. The
   * builder stamps these onto the mutation input unchanged.
   *
   * Structural invariant: the builder does NOT re-derive
   * `responseResult` / `responseStatus` from the cached parent
   * response. For a parent with P/F sub-questions, that cached
   * value is a DERIVED aggregate whose re-assertion cascades FAIL
   * to unanswered siblings on the next server poll.
   */
  readonly topLevel: TopLevelSaveFields;
  /**
   * Cached parent response for parent-BLOCK preservation only
   * (comment / naReason label / mediaIds / taskIds). Typed as
   * {@link PreviousResponseForSave} so the untrustworthy
   * `responseResult` is unreachable from within the builder.
   */
  readonly previousResponse: PreviousResponseForSave | undefined;
  /**
   * Existing sub-question responses on the parent. Every entry is
   * projected onto `subResponses` on the outgoing input via the
   * shared {@link projectSubResponsesForPreservation}, with only
   * its NA-reason slot overlayed from the draft (when present).
   * Undefined / empty is fine — the parent NA-picker path
   * doesn't need any sub rows.
   */
  readonly subQuestionResponses: readonly SubQuestionResponseEntry[] | undefined;
}

/**
 * Builds the mutation input for an NA-reason autosave.
 *
 * Pure — no react, no side effects. Consumed by
 * {@link useNaReasonDraft} which owns the debounce (0 ms —
 * immediate-save on selection), single-flight guard, cache
 * patch, and flush semantics.
 *
 * Design contract mirrors {@link buildCommentUpdate}:
 *
 *   1. Top-level answer fields arrive pre-computed via `topLevel`
 *      from {@link deriveTopLevelFieldsForSave}. The builder never
 *      touches `previousResponse.responseResult` directly — that
 *      field is omitted from its `previousResponse` type so the
 *      leak is unreachable structurally.
 *
 *   2. Parent naReason tri-state. The draft slot at the parent
 *      questionId, threaded into {@link buildPreservedResponseData}'s
 *      `naReasonOverride`, drives the parent block:
 *        undefined / absent → preserve persisted label (no id).
 *        null               → clear (`naReason` + `naReasonId`
 *                             dropped from the wire).
 *        {reasonId, label}  → replace both.
 *      The comment / mediaIds / taskIds preserve automatically.
 *
 *   3. Sub-response naReason tri-state. Every existing
 *      `SubQuestionResponseEntry` passes through the shared
 *      {@link projectSubResponsesForPreservation} with
 *      `naReasonOverrides = naReasonDraft`. The helper applies
 *      the same per-slot tri-state as the parent block, per sub.
 *      Comment / result / media / tasks preserve automatically.
 *
 *   4. NO synthetic sub-response entries. The NA-reason picker
 *      is only visible when the sub has already been answered
 *      NA — its response row already exists in the summary
 *      cache by the time the picker can fire. If it doesn't (a
 *      race where the sub-answer optimistic patch rolled back
 *      but the reason save is still in flight), the projection
 *      drops the reason on the floor and the wire carries only
 *      the parent block. That matches the "sub-answer never
 *      landed" state.
 *
 *   5. `expectedLastUpdatedAt` is NOT set. Conflict detection is
 *      the CR-V11 slice; the summary payload's
 *      `QuestionResponseDetails` doesn't carry a per-record
 *      token today.
 */
export function buildNaReasonUpdate(
  args: BuildNaReasonUpdateArgs,
): UpdateAuditQuestionResponseCommandInput {
  const {
    businessUnitId,
    auditId,
    questionId,
    alias,
    naReasonDraft,
    topLevel,
    previousResponse,
    subQuestionResponses,
  } = args;

  const input: UpdateAuditQuestionResponseCommandInput = {
    businessUnitId,
    auditId,
    questionId,
    responseStatus: topLevel.responseStatus,
    respondedBy: alias,
    lastUpdatedBy: alias,
  };
  if (topLevel.responseResult !== undefined) {
    input.responseResult = topLevel.responseResult;
  }

  // Parent block. `readParentSlot(naReasonDraft, questionId)`
  // returns `NaReasonOverride | undefined` — the widened
  // {@link buildPreservedResponseData} interprets undefined as
  // preserve, null as clear, populated as replace.
  const parentBlock = buildPreservedResponseData(previousResponse, {
    naReasonOverride: readParentSlot(naReasonDraft, questionId),
  });
  if (parentBlock) {
    input.response = parentBlock;
  }

  // Sub-responses. The shared projection applies the same
  // tri-state per sub via the naReasonOverrides map. Parent
  // keys in the map are naturally ignored — projection iterates
  // sub-response entries and reads by subQuestionId.
  const projected = projectSubResponsesForPreservation(subQuestionResponses, {
    naReasonOverrides: naReasonDraft,
  });
  if (projected.length > 0) {
    input.subResponses = projected;
  }

  return input;
}

/**
 * Safe reader for the parent-level slot of the naReason draft.
 * Isolates the dynamic-key access so the
 * security/detect-object-injection disable-comment lives in one
 * place. Callers get `NaReasonOverride | undefined` verbatim —
 * the preserve-vs-clear-vs-set distinction is preserved.
 */
function readParentSlot(
  draft: Readonly<Record<string, NaReasonOverride>>,
  key: string,
): NaReasonOverride | undefined {
  // eslint-disable-next-line security/detect-object-injection
  return draft[key];
}
